import { useEffect, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useLocation } from "react-router-dom";

const INACTIVITY_LIMIT = 30 * 60 * 1000;
const WARNING_TIME = 60 * 1000;
const MAX_SESSION = 8 * 60 * 60 * 1000;

export default function SessionTimeout({ onLogout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isLoggedOut, setIsLoggedOut] = useState(false);

  const inactivityTimer = useRef(null);
  const warningInterval = useRef(null);
  const maxSessionTimer = useRef(null);
  const isTabActive = useRef(true);
  const bc = useRef(null);

  const alertAudio = useRef(null);
  const location = useLocation();
  useEffect(() => {
    if (isLoggedOut || location.pathname === "/login") return;
    bc.current = new BroadcastChannel("session_channel");
    // alertAudio.current = new Audio("/alert10s.mp3");

    initializeTimers();
    setupListeners();

    return () => {
      cleanupAll();
    };
  }, [isLoggedOut]);

  const initializeTimers = () => {
    const loginTime = localStorage.getItem("LOGIN_TIME");
    if (!loginTime) localStorage.setItem("LOGIN_TIME", Date.now());
    startInactivityTimer();
    startMaxSessionTimer();
  };

  const setupListeners = () => {
    const events = ["mousemove", "click", "keydown", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, handleUserActivity));
    window.addEventListener("storage", onStorageReceive);
    window.addEventListener("visibilitychange", handleVisibilityChange);
    bc.current.onmessage = (msg) => {
      if (msg.data === "logout") forceLogout(false);
    };
  };

  const cleanupAll = () => {
    const events = ["mousemove", "click", "keydown", "scroll", "touchstart"];
    events.forEach((ev) => window.removeEventListener(ev, handleUserActivity));
    window.removeEventListener("storage", onStorageReceive);
    window.removeEventListener("visibilitychange", handleVisibilityChange);
    clearTimeout(inactivityTimer.current);
    clearTimeout(maxSessionTimer.current);
    clearInterval(warningInterval.current);

    try {
      bc.current?.close();
    } catch {
      /* empty */
    }
  };

  const handleUserActivity = () => {
    if (isLoggedOut) return;

    if (showWarning) resetWarning();
    startInactivityTimer();
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      isTabActive.current = false;
      clearTimeout(inactivityTimer.current);
    } else {
      isTabActive.current = true;
      startInactivityTimer();
    }
  };

  const startInactivityTimer = () => {
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (!isTabActive.current) return;
      startWarning();
    }, INACTIVITY_LIMIT);
  };

  const startMaxSessionTimer = () => {
    const loginTimestamp = Number(localStorage.getItem("LOGIN_TIME"));
    const elapsed = Date.now() - loginTimestamp;

    if (elapsed >= MAX_SESSION) return forceLogout();

    maxSessionTimer.current = setTimeout(() => {
      forceLogout();
    }, MAX_SESSION - elapsed);
  };

  const startWarning = () => {
    if (isLoggedOut) return;
    setShowWarning(true);
    setCountdown(60);

    warningInterval.current = setInterval(() => {
      setCountdown((prev) => {
        //if (prev === 11) alertAudio.current.play();
        if (prev <= 1) {
          clearInterval(warningInterval.current);
          forceLogout();
        }

        return prev - 1;
      });
    }, 1000);
  };

  const resetWarning = () => {
    setShowWarning(false);
    clearInterval(warningInterval.current);
    startInactivityTimer();
  };
  const onStorageReceive = (e) => {
    if (e.key === "LOGOUT_TRIGGER") forceLogout(false);
  };

  const forceLogout = (trigger = true) => {
    if (isLoggedOut) return;
    setIsLoggedOut(true);
    setShowWarning(false);
    cleanupAll();

    if (trigger) {
      try {
        bc.current?.postMessage("logout");
      } catch {
        /* empty */
      }
      localStorage.setItem("LOGOUT_TRIGGER", Date.now());
    }
    cleanupAll();
    onLogout();
  };

  if (isLoggedOut) return null;

  return (
    <>
      {showWarning && (
        <Modal show centered className="beauty-popup">
          <Modal.Header>
            <Modal.Title>Session Timeout Warning</Modal.Title>
          </Modal.Header>

          <Modal.Body>You will be logged out in {countdown} seconds due to inactivity.</Modal.Body>

          <Modal.Footer>
            <Button variant="primary" onClick={resetWarning}>
              Continue Session
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}
